import { z } from 'zod';

import { updateProfileFieldsSchema } from './profile.js';
import { SUPPORTED_LOCALES } from './locale.js';

/** `POST /v1/auth/telegram` request. `initData` is the raw string from the Telegram SDK. */
export const telegramAuthRequestSchema = z.object({
	initData: z.string().min(1).max(4096),
});

export type TelegramAuthRequest = z.infer<typeof telegramAuthRequestSchema>;

/**
 * The signed-in user as they see themselves. Never contains `telegram_username` or the Telegram
 * id. The birth date is their own (D48: editable in the profile); others see only the age.
 */
export const meSchema = z.object({
	id: z.uuid(),
	displayName: z.string(),
	locale: z.enum(SUPPORTED_LOCALES),
	role: z.enum(['user', 'moderator', 'admin']),
	botCanMessage: z.boolean(),
	/** Whether we already asked for bot messages (D24): the prompt is shown once. */
	botAccessAsked: z.boolean(),
	/** The current Terms are accepted (first-launch consent, T-007, D48). */
	onboardingCompleted: z.boolean(),
	/** Accepted Terms version; null until the consent screen is completed. */
	termsVersion: z.number().int().nullable(),
	/** Computed from the private birth date; null while none is set. */
	age: z.number().int().nullable(),
	/** `YYYY-MM-DD`, optional (D48). */
	birthDate: z.string().nullable(),
});

export type Me = z.infer<typeof meSchema>;

/** `PATCH /v1/me`: any subset of the profile (S14), the optional birth date included (D48). */
export const updateMeRequestSchema = z.strictObject({ ...updateProfileFieldsSchema }).refine(
	// JSON has no `undefined`: a key that is present is a change.
	(body) => Object.keys(body).length > 0,
	'nothing to update',
);

export type UpdateMeRequest = z.infer<typeof updateMeRequestSchema>;

export const telegramAuthResponseSchema = z.object({
	/** JWT, 12 h. Keep in memory only; on 401 repeat the Telegram auth. */
	accessToken: z.string(),
	user: meSchema,
	isNewUser: z.boolean(),
	/** A deleted account (T-804) came back: re-filled from Telegram, onboarding again. */
	restored: z.boolean(),
	/** sha256(telegram_id + salt), computed server-side; use as the PostHog distinct id. */
	analyticsId: z.string().regex(/^[0-9a-f]{64}$/),
});

export type TelegramAuthResponse = z.infer<typeof telegramAuthResponseSchema>;

/** `DELETE /v1/me` (T-804): profile erased; the id stays and a later sign-in restores it. */
export const deleteMeResponseSchema = z.object({ deleted: z.literal(true) });

export type DeleteMeResponse = z.infer<typeof deleteMeResponseSchema>;
